import React from "react";

import firebase from "firebase/compat/app";
import "firebase/compat/analytics";
import { performance } from "../../../data/FirebaseApp";
import { trace } from "firebase/performance";
import {
  Box,
  TextField,
  InputAdornment,
  IconButton,
  Tabs,
  Tab,
  Chip,
  Stack,
  FormControlLabel,
  Switch,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useNavigate } from "react-router-dom";
import { Routes, Route } from "react-router-dom";
import { confirmAlert } from "react-confirm-alert"; // Import
import "react-confirm-alert/src/react-confirm-alert.css"; // Import css
import DateRangePicker from "../../../components/molecules/DateRangePicker";
import ReportSummaryDrawer from "./reports/ReportSummaryDrawer";
import ReportTableContainer from "../../../components/organisms/ReportTableContainer";
import useEvents from "../../../hooks/useEvents";
import {
  ReportStates,
  Reports,
  CurrentReportFilter,
  ReportsLoading,
} from "../../../hooks/useReportHistory";
import ReportData from "../../../data/ReportData";
import { useRecoilValue, useRecoilState } from "recoil";
import organizationIdSelector from "../../../atoms/organizationIdSelector";
import userDataState from "../../../atoms/userDataAtom";
import PageHeader from "../../../components/PageHeader";
import PageContent from "../../../components/PageContent";

// Report List Component - extracted from main component
const ReportsList = () => {
  const navigate = useNavigate();
  const events = useEvents();
  const organizationId = useRecoilValue(organizationIdSelector);
  const userData = useRecoilValue(userDataState);

  const [currentReportFilter, setCurrentReportFilter] =
    useRecoilState(CurrentReportFilter);
  const [searchQuery, setSearchQuery] = React.useState("");
  const [activeQueueFilter, setActiveQueueFilter] = React.useState("all");
  const [showOnlyMyProcessed, setShowOnlyMyProcessed] = React.useState(false);
  const reports = useRecoilValue(Reports);
  const reportsLoading = useRecoilValue(ReportsLoading);

  // Filter reports based on search query
  const filteredReports = React.useMemo(() => {
    const query = searchQuery.toLowerCase().trim();
    let nextReports = reports;

    if (query) {
      nextReports = nextReports.filter((report) => {
        const firstName = (report.clientFirstName || "").toLowerCase();
        const lastName = (report.clientLastName || "").toLowerCase();
        const fullName = `${firstName} ${lastName}`.trim();
        return (
          firstName.includes(query) ||
          lastName.includes(query) ||
          fullName.includes(query)
        );
      });
    }

    if (currentReportFilter.state === ReportStates.unprocessed) {
      const now = new Date();
      nextReports = nextReports.filter((report) => {
        const createdOn = new Date(report.createdOn);
        if (activeQueueFilter === "overdue") {
          return now.getTime() - createdOn.getTime() >= 24 * 60 * 60 * 1000;
        }
        if (activeQueueFilter === "today") {
          return (
            createdOn.getFullYear() === now.getFullYear() &&
            createdOn.getMonth() === now.getMonth() &&
            createdOn.getDate() === now.getDate()
          );
        }
        return true;
      });
    }

    if (currentReportFilter.state === ReportStates.processed && showOnlyMyProcessed) {
      nextReports = nextReports.filter(
        (report) => report.processorUser?.uid === userData?.uid,
      );
    }

    return nextReports;
  }, [
    reports,
    searchQuery,
    currentReportFilter.state,
    activeQueueFilter,
    showOnlyMyProcessed,
    userData?.uid,
  ]);

  const canLoadMorePreviousReports =
    currentReportFilter.state === ReportStates.processed &&
    reports.length >= (currentReportFilter.limit || 0);

  return (
    <PageContent
      header={
        <PageHeader 
          title="Reports" 
          subtitle="Generate detailed reports and analytics for your coaching practice"
        />
      }
    >
      <Box
        sx={{
          display: "flex",
          flex: 1,
          flexDirection: "column",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            mb: 1.25,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            gap: 1.5,
            flexWrap: "wrap",
          }}
        >
          <Tabs
            value={currentReportFilter.state}
            onChange={(_, value) => {
              setCurrentReportFilter((previousState) => ({
                ...previousState,
                state: value,
              }));
              events.logButtonClick({
                buttonName:
                  value === ReportStates.unprocessed
                    ? "View Needs Review"
                    : "View Previous Reports",
                pageName: "Reports",
              });
            }}
            sx={{ minHeight: 40 }}
          >
            <Tab
              label="Needs Review"
              value={ReportStates.unprocessed}
              sx={{ minHeight: 40 }}
            />
            <Tab
              label="Previous Reports"
              value={ReportStates.processed}
              sx={{ minHeight: 40 }}
            />
          </Tabs>
          <TextField
            placeholder="Search reports by client name..."
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(event) => {
              setSearchQuery(event.target.value);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchQuery && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchQuery("");
                    }}
                    aria-label="Clear search"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{
              minWidth: { xs: "100%", sm: 300 },
              maxWidth: 420,
              flex: { xs: "1 1 100%", sm: "0 1 420px" },
            }}
          />
        </Box>
        {currentReportFilter.state === ReportStates.processed && (
          <Box sx={{ mb: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
              <DateRangePicker
                startDate={currentReportFilter.start}
                endDate={currentReportFilter.end}
                onChangeDates={({ startDate, endDate }) => {
                  setCurrentReportFilter((previousState) => ({
                    ...previousState,
                    start: startDate,
                    end: endDate,
                  }));
                }}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={showOnlyMyProcessed}
                    onChange={(event) => {
                      setShowOnlyMyProcessed(event.target.checked);
                    }}
                    size="small"
                  />
                }
                label="Only processed by me"
              />
            </Stack>
          </Box>
        )}
        {currentReportFilter.state === ReportStates.unprocessed && (
          <Box sx={{ mt: 1, mb: 1 }}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2" color="text.secondary">
                Queue:
              </Typography>
              <Chip
                label="All"
                color={activeQueueFilter === "all" ? "primary" : "default"}
                onClick={() => setActiveQueueFilter("all")}
                size="small"
              />
              <Chip
                label="Overdue (24h+)"
                color={activeQueueFilter === "overdue" ? "primary" : "default"}
                onClick={() => setActiveQueueFilter("overdue")}
                size="small"
              />
              <Chip
                label="Submitted Today"
                color={activeQueueFilter === "today" ? "primary" : "default"}
                onClick={() => setActiveQueueFilter("today")}
                size="small"
              />
            </Stack>
          </Box>
        )}

        <ReportTableContainer
          loading={reportsLoading}
          state={currentReportFilter.state}
          title={
            currentReportFilter.state === ReportStates.unprocessed
              ? "Needs Review"
              : "Previous Reports"
          }
          reports={filteredReports}
          showAlertCount={
            currentReportFilter.state === ReportStates.unprocessed
              ? true
              : false
          }
          reportClicked={(report) => {
            // Navigate to report summary instead of opening drawer
            if (!report?.id) {
              console.error("Report object is missing or has no id:", report);
              // Try to navigate anyway if we have any identifier
              return;
            }
            navigate(`/home/reports/summary/${organizationId}/${report.id}`);
            events.logButtonClick({
              buttonName: "View Report",
              pageName: "Reports",
              tabName: report.id,
            });
          }}
          processReportClicked={(report) => {
            ReportData.processReport({ reportId: report.id });
            events.logButtonClick({
              buttonName: "Process Report From List",
              pageName: "Reports",
              tabName: report.id,
            });
          }}
          deleteReportClicked={(report) => {
            confirmAlert({
              title: `Delete this report?`,
              message: "This cannot be undone. Continue with delete?",
              buttons: [
                {
                  label: "Yes",
                  onClick: async () => {
                    await ReportData.deleteReport(
                      localStorage.getItem("uid"),
                      report.id,
                    );
                    events.logButtonClick({
                      buttonName: "Delete Report",
                      pageName: "Reports",
                    });
                  },
                },
                {
                  label: "No",
                  onClick: () => {},
                },
              ],
            });
          }}
        />
        {canLoadMorePreviousReports && (
          <Box display="flex" justifyContent="center" mt={2}>
            <Chip
              label="Load Older Reports"
              color="primary"
              onClick={() => {
                setCurrentReportFilter((previousState) => ({
                  ...previousState,
                  limit: (previousState.limit || 0) + 50,
                }));
              }}
              variant="outlined"
            />
          </Box>
        )}
      </Box>
    </PageContent>
  );
};

const ReportPage = () => {
  const t = trace(performance, "REPORT_PAGE");
  t.start();

  React.useEffect(() => {
    firebase.analytics().logEvent("page_view", {
      page_title: "Report Page",
      page_location: "/reports",
    });
  }, []);

  t.stop();

  return (
    <Routes>
      <Route path="" element={<ReportsList />} />
      <Route
        path="summary/:organizationId/:reportId"
        element={<ReportSummaryDrawer />}
      />
    </Routes>
  );
};

export default ReportPage;
